import { ELayerType } from './ELayerType';

/**
 * What kind of thing a layer is: what came in, and which run produced the rest.
 *
 * Layers used to sort by name alone, which interleaved the groups — a
 * diagenesis run listed "Bacteria in sediment", then bathymetry, current
 * velocity and the deposition output, then the remaining six sediment layers.
 * Sorting by group first and name second keeps each run's output together.
 *
 * The groups match the Model options in the simulation tab, so a layer's
 * subtitle names the run that made it.
 */
type TLayerGroup = 'input' | 'deposition' | 'diagenesis' | 'dispersion' | 'pathogen';

interface ILayerGroupInfo {
  /** Position in the layer list. Layers sort by this, then by name. */
  order: number;
  /** Shown as the layer's subtitle. */
  label: string;
  /** Faint wash over the whole row. */
  tint: string;
  /** Solid bar down the row's left edge. */
  edge: string;
}

/**
 * Order, label and colour together, so the whole scheme can be tuned in one
 * place.
 *
 * The tint has to stay faint: ListView renders a row's decorator *after* its
 * cells, so it paints on top of the text rather than behind it, and much past
 * an alpha of ~0.15 the labels start to wash out. The edge bar carries the
 * signal and can be widened freely.
 */
const LAYER_GROUP_INFO: Record<TLayerGroup, ILayerGroupInfo> = {
  input:      { order: 1, label: 'Input',      tint: 'rgba(148, 163, 184, 0.10)', edge: '#94a3b8' },  // slate
  deposition: { order: 2, label: 'Deposition', tint: 'rgba(245, 158,  11, 0.13)', edge: '#f59e0b' },  // amber
  diagenesis: { order: 3, label: 'Diagenesis', tint: 'rgba(168, 128,  80, 0.16)', edge: '#a88050' },  // earth
  dispersion: { order: 4, label: 'Dispersion', tint: 'rgba( 56, 189, 248, 0.12)', edge: '#38bdf8' },  // sky
  pathogen:   { order: 5, label: 'Pathogens',  tint: 'rgba(192, 132, 252, 0.13)', edge: '#c084fc' },  // violet
};

/**
 * Exhaustive by construction: `Record<ELayerType, …>` means a new layer type
 * will not compile until it has been placed in a group.
 */
const LAYER_GROUPS: Record<ELayerType, TLayerGroup> = {
  // Loaded by the user or the system, not computed.
  [ELayerType.Depth]:                   'input',
  [ELayerType.CurrentSpeed]:            'input',

  // Particulate matter reaching the seabed.
  [ELayerType.DailyPOC]:                'deposition',
  [ELayerType.DailyPOM]:                'deposition',
  [ELayerType.YearlyPOC]:               'deposition',

  // What the seabed does with it.
  [ELayerType.PocInSediment]:           'diagenesis',
  [ELayerType.PomInSediment]:           'diagenesis',
  [ELayerType.BacteriaInSediment]:      'diagenesis',
  [ELayerType.OxygenInSediment]:        'diagenesis',
  [ELayerType.SulphateInSediment]:      'diagenesis',
  [ELayerType.IronHydroxideInSediment]: 'diagenesis',
  [ELayerType.TotalSulphideInSediment]: 'diagenesis',
  [ELayerType.IronInSediment]:          'diagenesis',
  [ELayerType.IronSulphideInsediment]:  'diagenesis',

  // Carried dissolved in the water column. Therapeutants sit here because they
  // disperse like the rest, though they arrive with a treatment rather than
  // with the fish.
  [ELayerType.Oxygen]:                  'dispersion',
  [ELayerType.Ammonia]:                 'dispersion',
  [ELayerType.Chlorophyll]:             'dispersion',
  [ELayerType.Therapeutants]:           'dispersion',

  // Its own model, so its own group.
  [ELayerType.Pathogen]:                'pathogen',
};

const layerGroupFor = (type: ELayerType): TLayerGroup => LAYER_GROUPS[type];

const layerGroupInfoFor = (type: ELayerType): ILayerGroupInfo => LAYER_GROUP_INFO[layerGroupFor(type)];

export { TLayerGroup, ILayerGroupInfo, LAYER_GROUPS, LAYER_GROUP_INFO, layerGroupFor, layerGroupInfoFor }
